import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Plants from "@/pages/Plants";
import PlantDetail from "@/pages/PlantDetail";
import ServiceRequired from "@/pages/ServiceRequired";
import Alerts from "@/pages/Alerts";
import Reports from "@/pages/Reports";
import AdminUsers from "@/pages/AdminUsers";
import SystemHealth from "@/pages/SystemHealth";
import Customers from "@/pages/Customers";
import InviteUsers from "@/pages/InviteUsers";
import AlertDetail from "@/pages/AlertDetail";
import Profile from "@/pages/Profile";
import CustomerLayout from "@/pages/customer/CustomerLayout";
import MySolar from "@/pages/customer/MySolar";
import Support from "@/pages/customer/Support";
import Documents from "@/pages/customer/Documents";
import CustomerReports from "@/pages/customer/Reports";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import CreatePlant from "@/pages/CreatePlant";

function Protected({ children, allowedRoles }) {
    const { user, checking } = useAuth();
    if (checking) return <div className="p-6 text-sm text-zinc-500">Loading session…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (allowedRoles && !allowedRoles.includes(user.role)) {
        // Customers always redirect to their portal; other unauthorised users fall back to admin.
        return <Navigate to={user.role === "customer" ? "/customer/dashboard" : "/"} replace />;
    }
    return children;
}

// Root landing decision — customers get their portal; ops/admin gets the console.
function RootRedirect() {
    const { user, checking } = useAuth();
    if (checking) return <div className="p-6 text-sm text-zinc-500">Loading session…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (user.role === "customer") return <Navigate to="/customer/dashboard" replace />;
    return <Layout />;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <Toaster position="top-right" richColors />
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/set-password" element={<SetPassword />} />

                        {/* Customer Portal — isolated, role-guarded */}
                        <Route
                            path="/customer"
                            element={
                                <Protected allowedRoles={["customer", "super_admin", "sales"]}>
                                    <CustomerLayout />
                                </Protected>
                            }
                        >
                            <Route index element={<Navigate to="/customer/dashboard" replace />} />
                            <Route path="dashboard" element={<MySolar />} />
                            <Route path="documents" element={<Documents />} />
                            <Route path="support" element={<Support />} />
                            <Route path="reports" element={<CustomerReports />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>

                        {/* Admin / Ops console — customers never see this shell */}
                        <Route path="/" element={<RootRedirect />}>
                            <Route index element={
                                <Protected allowedRoles={["super_admin", "sales"]}><Dashboard /></Protected>
                            } />
                            <Route path="plants" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><Plants /></Protected>
                            } />
                            <Route path="plants/:id" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><PlantDetail /></Protected>
                            } />
                            <Route path="service-required" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><ServiceRequired /></Protected>
                            } />
                            <Route path="alerts" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><Alerts /></Protected>
                            } />
                            <Route path="alerts/:alertId" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><AlertDetail /></Protected>
                            } />
                            <Route path="reports" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><Reports /></Protected>
                            } />
                            <Route path="customers" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><Customers /></Protected>
                            } />
                            <Route path="admin/users" element={
                                <Protected allowedRoles={["super_admin"]}><AdminUsers /></Protected>
                            } />
                            <Route path="admin/plants/new" element={
                                <Protected allowedRoles={["super_admin", "sales"]}><CreatePlant /></Protected>
                            } />
                            <Route path="admin/invite" element={
                                <Protected allowedRoles={["super_admin"]}><InviteUsers /></Protected>
                            } />
                            <Route path="admin/system-health" element={
                                <Protected allowedRoles={["super_admin"]}><SystemHealth /></Protected>
                            } />
                            <Route path="profile" element={<Protected><Profile /></Protected>} />
                        </Route>
                    </Routes>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
